import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import {
  useGetCourseCompletionQuery,
  useGetSingleCourseAdminSideQuery,
  useUpdateCourseBasicInformationMutation,
} from "../services/course/courseApi";
import CourseCompletionSection from "../components/course-details-view/CourseCompletionSection";
import BasicInformationSection from "../components/course-details-view/BasicInformationSection";
import CourseOverviewSection from "../components/course-details-view/CourseOverviewSection";
import LearningOutcomesSection from "../components/course-details-view/LearningOutcomesSection";
import CourseInstructorsSection from "../components/course-details-view/CourseInstructorsSection";
import CourseMaterialsSection from "../components/course-details-view/CourseMaterialsSection";
import CourseModulesSection from "../components/course-details-view/CourseModulesSection";
import CourseFaqSection from "../components/course-details-view/CourseFaqSection";
import CourseDetailsActionsSection from "../components/course-details-view/CourseDetailsActionsSection";
import { showToast } from "../utils/toast";

const BASIC_INFO_REQUIREMENT_KEYS = [
  "courseTitle",
  "courseEnrollementType",
  "category",
  "courseThumbnail",
  "courseOutline",
  "subCategory",
  "ageGroup",
  "level",
  "courseAccess",
  "coursePrice",
];

const OVERVIEW_REQUIREMENT_KEYS = [
  "courseDescription",
  "courseDuration",
  "coursePrerequisite",
  "courseTargetAudience",
];

const getToneFromCompletedCount = (completedCount, totalCount) => {
  if (totalCount <= 0) return "danger";
  if (completedCount === totalCount) return "success";
  if (completedCount > 0) return "warning";
  return "danger";
};

const getToneFromRequirementGroup = (group) => {
  if (!group) return "danger";
  if (group.completed) return "success";
  if (group.total > 0) return "warning";
  return "danger";
};

const CourseDetails = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [basicInfoChanges, setBasicInfoChanges] = useState({});
  const [overviewChanges, setOverviewChanges] = useState({});

  const pendingChanges = React.useMemo(
    () => ({
      ...basicInfoChanges,
      ...overviewChanges,
    }),
    [basicInfoChanges, overviewChanges],
  );

  const hasChanges = Object.keys(pendingChanges).length > 0;
  const {
    data: courseResponse,
    isLoading,
    isError,
  } = useGetSingleCourseAdminSideQuery(courseId, {
    skip: !courseId,
  });

  const courseData = courseResponse?.data?.[0] || null;

  const { data: courseCompletionResponse } = useGetCourseCompletionQuery(
    courseId,
    { skip: !courseId },
  );

  const [updateCourseBasicInformation] =
    useUpdateCourseBasicInformationMutation();

  const [isHeaderSaving, setIsHeaderSaving] = useState(false);
  const [isBottomSaving, setIsBottomSaving] = useState(false);

  const [editableCourse, setEditableCourse] = useState(null);

  useEffect(() => {
    setEditableCourse(courseData);
  }, [courseData]);

  const completionSummary = React.useMemo(() => {
    const completionData = courseCompletionResponse?.data?.[0] || null;
    const requirements = completionData?.courseRequirements || {};
    const publishRequirements = completionData?.publishRequirements || {};

    const basicInfoCompleted = BASIC_INFO_REQUIREMENT_KEYS.filter(
      (key) => requirements[key] === true,
    ).length;

    const overviewCompleted = OVERVIEW_REQUIREMENT_KEYS.filter(
      (key) => requirements[key] === true,
    ).length;

    // Instructors are intentionally ignored on the frontend for now: no
    // badge, no missing-field message, and no publish blocker for it.
    const missingFields = (completionData?.missingFields || []).filter(
      (field) => !String(field).toLowerCase().includes("instructor"),
    );

    const canPublish = Object.entries(publishRequirements)
      .filter(([key]) => key !== "instructors")
      .every(([, value]) => Boolean(value));

    return {
      progress: completionData?.completionPercentage ?? 0,
      remainingFields: completionData?.remainingFields ?? 0,
      canPublish,
      missingFields,
      completionItems: [
        {
          label: "Basic Info",
          tone: getToneFromCompletedCount(
            basicInfoCompleted,
            BASIC_INFO_REQUIREMENT_KEYS.length,
          ),
        },
        {
          label: "Course Overview",
          tone: getToneFromCompletedCount(
            overviewCompleted,
            OVERVIEW_REQUIREMENT_KEYS.length,
          ),
        },
        {
          label: "Learning Outcomes",
          tone: getToneFromRequirementGroup(requirements.learningOutcomes),
        },
        {
          label: "Course Modules",
          tone: getToneFromRequirementGroup(requirements.modules),
        },
      ],
    };
  }, [courseCompletionResponse]);

  const saveCourseData = async () => {
    if (!courseId || !editableCourse) {
      showToast({
        type: "error",
        message: "Course information is not available.",
      });

      return false;
    }

    if (!hasChanges) {
      return false;
    }

    const hasThumbnailFile = pendingChanges.courseThumbnail instanceof File;

    const payload = hasThumbnailFile
      ? Object.entries(pendingChanges).reduce((formData, [key, value]) => {
          if (key === "courseThumbnail" && value instanceof File) {
            formData.append("files", value);
          } else if (value !== undefined && value !== null) {
            formData.append(key, value);
          }

          return formData;
        }, new FormData())
      : { ...pendingChanges };

    try {
      const result = await updateCourseBasicInformation({
        courseId,
        payload,
      }).unwrap();

      if (result?.success === false) {
        showToast({
          type: "error",
          message: result.message || "Failed to save course information.",
        });

        return false;
      }

      showToast({
        type: "success",
        message: result?.message || "Course information saved successfully.",
      });

      setBasicInfoChanges({});
      setOverviewChanges({});

      return true;
    } catch (error) {
      console.error("Failed to save course:", error);

      showToast({
        type: "error",
        message:
          error?.data?.message ||
          error?.message ||
          "Failed to save course information.",
      });

      return false;
    }
  };

  const handleSaveCourse = async () => {
    setIsHeaderSaving(true);

    try {
      return await saveCourseData();
    } finally {
      setIsHeaderSaving(false);
    }
  };
  const handleBottomSave = async () => {
    setIsBottomSaving(true);

    try {
      return await saveCourseData();
    } finally {
      setIsBottomSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <section className="min-h-screen bg-[#D9D9D914] pl-[34px] py-6 pr-5 font-['Roboto'] flex items-center justify-center">
          <p className="text-lg text-[#4A4A68]">Loading course details...</p>
        </section>
      </AdminLayout>
    );
  }

  if (isError || !courseData) {
    return (
      <AdminLayout>
        <section className="min-h-screen bg-[#D9D9D914] pl-[34px] py-6 pr-5 font-['Roboto'] flex items-center justify-center">
          <p className="text-lg text-[#991B1B]">Failed to load course details</p>
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <section className="min-h-screen bg-[#D9D9D914] px-5 2xl:pl-[34px] py-6 2xl:pr-5 font-['Roboto']">
        <div className="w-full max-w-[1520px] bg-[#FFFFFF9C] px-3 md:px-6 lg:px-8 2xl:px-10 pb-8 lg:pb-14 2xl:pb-20 pt-4 ">
          <CourseCompletionSection
            progress={completionSummary.progress}
            completionItems={completionSummary.completionItems}
            onBack={() => navigate(-1)}
            hasChanges={hasChanges}
            onSave={handleSaveCourse}
            isSaving={isHeaderSaving}
          />

          <BasicInformationSection
            courseData={editableCourse}
            onChange={(changes) => {
              setEditableCourse((current) => ({
                ...(current || {}),
                ...changes,
              }));

              setBasicInfoChanges((current) => ({
                ...current,
                ...changes,
              }));
            }}
          />
          <CourseOverviewSection
            courseData={editableCourse}
            onChange={(changes) => {
              setEditableCourse((current) => ({
                ...(current || {}),
                ...changes,
              }));

              setOverviewChanges((current) => ({
                ...current,
                ...changes,
              }));
            }}
          />

          <LearningOutcomesSection courseId={courseId} />
          <CourseModulesSection courseId={courseId} courseData={courseData} />
          <CourseMaterialsSection courseId={courseId} courseData={courseData} />

          {/* as bakcend api is still in progress, so for now we hide this section */}
          {/* <CourseFaqSection courseData={courseData} /> */}
        </div>
        <CourseInstructorsSection courseId={courseId} courseData={courseData} />
        <CourseDetailsActionsSection
          progress={completionSummary.progress}
          remainingFields={completionSummary.remainingFields}
          canPublish={completionSummary.canPublish}
          missingFields={completionSummary.missingFields}
          courseId={courseId}
          courseData={editableCourse}
          hasChanges={hasChanges}
          onSave={handleBottomSave}
          isSaving={isBottomSaving}
        />
      </section>
    </AdminLayout>
  );
};

export default CourseDetails;
